import { Button, Tag, type Column } from '@dayon/design-system';
import { formatDay, stateDetail } from './format.ts';
import { SELF_EXCLUSION_RULE, sentence } from './vocabulary.ts';
import type { ControlRow } from './types.ts';

/**
 * Account controls, as a table.
 *
 * The action column is built only when the role holds players.restrict —
 * "hidden, not disabled, without it". Inside it the control is live for every
 * kind except a player's own self-exclusion, which is stated as the rule rather
 * than rendered as a disabled switch with no explanation.
 */
export function restrictionColumns(
  canRestrict: boolean,
  onLift: (row: ControlRow) => void,
  busyId: string | null,
): Column<ControlRow>[] {
  const columns: Column<ControlRow>[] = [
    {
      key: 'control',
      header: 'Control',
      render: (c) => (
        <>
          {c.label}
          <div className="text-subtle" style={{ fontSize: '11.5px' }}>
            {c.description}
          </div>
        </>
      ),
    },
    {
      key: 'setBy',
      header: 'Set by',
      secondary: true,
      // PLAYER · STAFF · SYSTEM. This is the column that makes a self-exclusion
      // non-liftable: a player-set lock is not a staff decision to reverse.
      render: (c) =>
        c.record ? (
          <span className="text-muted">{sentence(c.record.applied_by)}</span>
        ) : (
          <span className="text-subtle">—</span>
        ),
    },
    {
      key: 'since',
      header: 'Effective from',
      secondary: true,
      render: (c) =>
        c.record ? (
          <span className="text-muted tabular">{formatDay(c.record.effective_from)}</span>
        ) : (
          <span className="text-subtle">not on file</span>
        ),
    },
    {
      key: 'reason',
      header: 'Reason',
      secondary: true,
      render: (c) =>
        c.record?.reason ? (
          <span className="text-muted">{sentence(c.record.reason)}</span>
        ) : (
          <span className="text-subtle">—</span>
        ),
    },
    {
      key: 'state',
      header: 'State',
      // Tag plus the written word: state is never carried by the pill alone.
      // On means IN FORCE — a row whose expiry has passed reads Off, with the
      // day it lapsed beside it, because that is what the payout rail sees.
      render: (c) => {
        const detail = stateDetail(c.record);
        return (
          <>
            <Tag tone={c.on ? 'accent' : 'quiet'}>{c.on ? 'On' : 'Off'}</Tag>
            {detail ? (
              <div className="text-subtle tabular" style={{ fontSize: '11.5px' }}>
                {detail}
              </div>
            ) : null}
          </>
        );
      },
    },
  ];

  if (!canRestrict) return columns;

  return [
    ...columns,
    {
      key: 'action',
      header: 'Action',
      render: (c) => {
        if (!c.on || !c.record) return <span className="text-subtle">—</span>;
        if (c.locked) {
          return (
            <span className="text-subtle" title={SELF_EXCLUSION_RULE}>
              Not staff&rsquo;s to lift
            </span>
          );
        }
        return (
          <Button
            small
            disabled={busyId !== null}
            aria-label={`Lift the ${c.label.toLowerCase()} on this player`}
            onClick={() => onLift(c)}
          >
            {busyId === c.record.id ? 'Lifting…' : 'Lift'}
          </Button>
        );
      },
    },
  ];
}
